import React from 'react';
import styled from 'styled-components';
import { Tooltip } from '@planne-software/uni';
import { Typography } from '@planne-software/uni/mui/material';
import { UniThemeProvider } from '@planne-software/uni/themes';
import { IconInfoCircle } from '@tabler/icons-react';
import useTranslate from '@src/i18n/useTranslate';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import { CurrencyInfo } from '@src/core/buyerApiClient';
import { LayoutColors } from '@src/core/theme';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR as localeText } from '@mui/x-date-pickers/locales';
import { ptBR } from 'date-fns/locale';
import useFeatureFlag from '@src/hooks/useFeatureFlag';

interface ServiceFeeDisplayProps {
  serviceFeeCurrencyInfo?: CurrencyInfo | null;
  serviceFeeDescription?: string;
}

export const ServiceFeeDisplay = ({ serviceFeeCurrencyInfo, serviceFeeDescription }: ServiceFeeDisplayProps) => {
  const { i18n } = useTranslate();
  const { formatCurrencyInfo } = useCurrencyFormat();
  const { isFeatureFlagEnabled } = useFeatureFlag();

  const isServiceFeeEnabled = isFeatureFlagEnabled('PR-647-service-fee');

  if (!isServiceFeeEnabled || !serviceFeeCurrencyInfo || serviceFeeCurrencyInfo.amountCents <= 0) {
    return null;
  }

  return (
    <Wrapper>
      <UniThemeProvider
        adapterLocale={ptBR}
        dateAdapter={AdapterDateFns}
        localeText={localeText.components.MuiLocalizationProvider.defaultProps.localeText}
        scoped
      >
        <Container>
          <Label>
            <Typography variant='text-md-regular'>{i18n('cart_serviceFee')}:</Typography>
            {serviceFeeDescription ? (
              <Tooltip
                arrow
                placement='top'
                label={serviceFeeDescription ?? i18n('cart_serviceFee_tooltip')}
                labelProps={{
                  sx: { fontFamily: 'Montserrat, sans-serif' },
                }}
              >
                <IconInfoCircle size={18} color={LayoutColors.blackMain} />
              </Tooltip>
            ) : null}
          </Label>
          <Typography variant='text-md-regular'>{formatCurrencyInfo(serviceFeeCurrencyInfo)}</Typography>
        </Container>
      </UniThemeProvider>
    </Wrapper>
  );
};

// Wrapper is needed to override default styles that causes break the layout
const Wrapper = styled.div`
  .MuiScopedCssBaseline-root {
    background: inherit;

    * {
      font-family: 'Montserrat', sans-serif;
    }
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 4px 0;
  width: 100%;
  color: ${LayoutColors.blackMain};
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;

  span {
    display: flex;
    align-items: center;

    &:last-child {
      cursor: help;
    }
  }
`;
